<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted} from 'vue';
import {SvgIcon} from '../../../svg.ts';
import EcadDocument from '../../../components/allspice_render/EcadDocument.vue';
import type {ProjectPageDiffPreview} from '../../types/previews.ts';
import type {Store} from '../../store.ts';

import {initViewedCheckboxListener} from './utils.ts';
import {FILE_DIFF_STATUS} from '../../types/file_tree.ts';
import PageLevelComments from './PageLevelComments.vue';
import {initDiffHeaderPopup} from '../../../features/repo-diff.ts';
import MultiPageComponentSearch from '../MultiPageComponentSearch.vue';
import {isMultiPageSchematic as checkMultiPage} from '../../utils/multi-page.ts';
const store = inject<Store>('store');

const preview = defineModel<ProjectPageDiffPreview>({
  type: Object,
  required: true,
});

const {commentHtml, pageComponent, isDiffLoadedPromise, file, projectData} = preview.value;
const cleanup: Array<() => void> = [];
const truthyPromise = Promise.resolve();
const symbol = pageComponent?.outputSymbols?.[0];

const isPullRequest = computed(() => {
  return Boolean(store.prInfo.number);
});

const newCommentURL = computed(() => {
  return `${store.repoLink}/pulls/${store.prInfo.number}/files/reviews/new_comment`;
});

const viewFileURL = computed(() => {
  return `${store.repoLink}/src/${store.refLinkSnippet}/${encodeURIComponent(file.basePath)}`;
});

const markAsViewed = computed(() => {
  return `${store.repoLink}/pulls/${store.prInfo.number}/viewed-files`;
});

const headCommit = computed(() => {
  return store.diffInfo.headCommit;
});

// Show the multi-page component search button only when this page belongs to
// a multi-page schematic file (parent node is a `multi-page-diff`).  Single
// pages of project files (`proj-diff`) already have their own per-page search
// via DocumentSearch.vue inside EcadDocument.
const isMultiPageSchematic = computed(() => {
  return checkMultiPage(file.nameHash, store.hashToPathMap.value);
});

onMounted(() => {
  const viewCheckboxCleanup = initViewedCheckboxListener(store);
  if (viewCheckboxCleanup) {
    cleanup.push(viewCheckboxCleanup);
  }
  initDiffHeaderPopup();
});

onUnmounted(() => {
  for (const cleanupFn of cleanup) {
    cleanupFn();
  }
});

</script>

<template>
  <div
    :id="`diff-${file.nameHash}`"
    ref="diffFileBoxRef"
    class="allspice-diff diff-file-box file-content has-selectable-region tab-size-4 tw:mt-0"
    :data-new-comment-url="newCommentURL"
    :data-base-path="pageComponent?.isAdded ? null : file.basePath"
    :data-path="file.headPath"
    :data-repository-owner="store.diffInfo.username"
    :data-repository-name="store.diffInfo.reponame"
    :data-base-commit="store.diffInfo.baseCommit"
    :data-head-commit="store.diffInfo.headCommit"
    :data-sub-path="file.pageId"
    :data-pr-index="store.prInfo.number"
    :data-accordion-content="pageComponent?.id"
    data-mounted="true"
  >
    <h4 class="diff-file-header sticky-2nd-row ui top attached header">
      <span class="tw:font-mono">{{ file.projectName }} > {{ file.name }}</span>
      <div class="diff-file-header-actions tw:flex tw:flex-1 tw:items-center tw:justify-end tw:gap-1 tw:flex-wrap tw:relative">
        <div
          v-if="isPullRequest"
          class="ui compact icon buttons"
        >
          <button class="ui basic tiny button select-region-pointer-mode active">
            Pointer
          </button>
          <button class="ui basic tiny button select-region-select-mode">
            Snippet
          </button>
        </div>
        <label
          v-if="isPullRequest"
          :data-link="markAsViewed"
          :data-headcommit="headCommit"
          class="viewed-file-form unselectable"
          :class="{'viewed-file-checked-form': file.isViewed}"
        >
          <input
            type="checkbox"
            :name="file.pageViewedPath"
            autocomplete="off"
            :checked="file.isViewed"
          > Viewed
        </label>
        <button class="btn diff-header-popup-btn tw:p-1">
          <SvgIcon
            name="octicon-kebab-horizontal"
            class="tw:flex tw:content-center tw:justify-center"
          />
        </button>
        <div class="tippy-target">
          <a
            class="item"
            rel="nofollow"
            :href="viewFileURL"
          >View File</a>
        </div>
        <MultiPageComponentSearch
          v-if="isMultiPageSchematic"
          :file="file"
        />
        <a
          class="review-file-comment"
          style="text-decoration: none;"
        >
          <span
            class="btn-octicon tooltip"
            data-tooltip-content="Comment on file"
            data-position="bottom-center"
            data-variation="inverted tiny"
          >
            <SvgIcon
              name="octicon-comment"
              class="tw:flex tw:content-center tw:justify-center"
            />
          </span>
        </a>
      </div>
    </h4>
    <div
      v-if="file.status === FILE_DIFF_STATUS.DELETED"
      class="ui negative message"
    >
      This file was deleted.
    </div>
    <div
      v-if="pageComponent"
      class="diff-file-body ui attached segment selectable-region-ancestor"
    >
      <EcadDocument
        :base-document="pageComponent?.base"
        :head-document="pageComponent?.head"
        :are-documents-loaded-promise="truthyPromise"
        :head-svg="symbol?.svgText"
        :is-diff-loaded-promise="isDiffLoadedPromise"
        :is-diff="true"
        :base-repo-owner="store.diffInfo.username"
        :base-repo-name="store.diffInfo.reponame"
        :base-commit="store.diffInfo.baseCommit"
        :base-tree-path="file.status === FILE_DIFF_STATUS.ADDED ? '' : file.basePath"
        :head-repo-owner="store.diffInfo.username"
        :head-repo-name="store.diffInfo.reponame"
        :head-commit="store.diffInfo.headCommit"
        :head-tree-path="file.headPath"
        :doc-id="symbol.docId"
        :initial-snippet-clickthrough="true"
        :project-data="projectData"
      />
      <PageLevelComments
        :code-conversations-container="commentHtml"
        :page-id="file.pageId"
      />
    </div>
  </div>
</template>
